import type { ApiErrorResponse } from '@/types'
import { getIdToken, requireLogin } from '@/auth/session'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

// Custom API error class
export class ApiError extends Error {
  status: number
  data: ApiErrorResponse

  constructor(status: number, data: ApiErrorResponse) {
    super(data.error.message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

// Base API request function with error handling
export async function apiRequest<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`

  async function send(token: string): Promise<Response> {
    const headers = new Headers(options?.headers)
    if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
    headers.set('Authorization', `Bearer ${token}`)
    return fetch(url, { ...options, headers })
  }
  const token = await getIdToken()
  if (!token) {
    await requireLogin()
    throw new ApiError(401, { error: { code: 'UNAUTHORIZED', message: 'Sign-in required' } })
  }
  let response = await send(token)
  if (response.status === 401) {
    const refreshed = await getIdToken(true)
    if (refreshed) response = await send(refreshed)
    if (!refreshed) {
      await requireLogin()
      throw new ApiError(401, { error: { code: 'UNAUTHORIZED', message: 'Sign-in required' } })
    }
  }

  if (response.status === 401) {
    throw new ApiError(401, { error: { code: 'UNAUTHORIZED', message: 'Sign-in required' } })
  }

  if (!response.ok) {
    let errorData: ApiErrorResponse
    try {
      const data = await response.json()
      errorData = data?.error?.message ? data : {
        error: {
          code: `HTTP_${response.status}`,
          message: typeof data?.message === 'string' ? data.message : `Request failed with status ${response.status}`,
        },
      }
    } catch {
      errorData = {
        error: {
          code: 'UNKNOWN_ERROR',
          message: `Request failed with status ${response.status}`,
        },
      }
    }
    throw new ApiError(response.status, errorData)
  }

  return response.json()
}
